/**
 * NewPasswordForm — first sign-in, where the temporary password is replaced
 *
 * AL-470: this screen is only reachable once Cognito has ACCEPTED the temporary
 * password and returned a NEW_PASSWORD_REQUIRED challenge. It previously told
 * the user their temporary password had expired, which stated a failure at the
 * moment the credential worked; users stopped reading and contacted their C1
 * instead of completing the form. The copy now describes the task.
 *
 * A genuinely expired temporary password fails earlier, at InitiateAuth, so it
 * never reaches this screen — that message belongs on the login form.
 */

import React from "react";
import type { PasswordRequirement } from "../../config/passwordPolicy";
import PasswordRequirements from "./PasswordRequirements";

interface NewPasswordFormProps {
  pendingNewPassword: string;
  setPendingNewPassword: (v: string) => void;
  pendingConfirmPassword: string;
  setPendingConfirmPassword: (v: string) => void;
  /** Requirements actually enforced by the pool, from checkPassword(). */
  requirements: PasswordRequirement[];
  /** True when every enforced rule passes. */
  isPasswordValid: boolean;
  isSubmitting: boolean;
  error: string;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
}

const NewPasswordForm: React.FC<NewPasswordFormProps> = ({
  pendingNewPassword,
  setPendingNewPassword,
  pendingConfirmPassword,
  setPendingConfirmPassword,
  requirements,
  isPasswordValid,
  isSubmitting,
  error,
  onSubmit,
  onBack,
}) => {
  const passwordsMatch =
    pendingNewPassword.length > 0 && pendingNewPassword === pendingConfirmPassword;
  // Gate on the real policy so a password Cognito would reject never leaves the
  // browser, and one it would accept is never blocked.
  const canSubmit = isPasswordValid && passwordsMatch && !isSubmitting;

  return (
    <>
      {/*
        AL-470 specifies both of these strings, so they are quoted from the
        ticket rather than paraphrased.

        The heading is unchanged because it was never the problem — the body was.
        The body leads with the account's state instead of a failure: nothing here
        has expired, been rejected or needs recovering, and saying otherwise at the
        moment the temporary password was accepted is what sent users to their C1.

        The phrasing follows the OPS enrolment page, which has carried the
        equivalent line without complaint: "This account still has its temporary
        password. Choose a permanent one to continue."
      */}
      <h1>Set New Password</h1>
      <p>Your account still has its temporary password. Choose a permanent one to continue.</p>
      {error && (
        <div className="error-message" data-testid="new-password-error">
          {error}
        </div>
      )}
      <form
        onSubmit={onSubmit}
        className="login-form"
        data-testid="new-password-form"
      >
        <div className="form-group">
          <label htmlFor="new-password">New Password</label>
          <input
            id="new-password"
            type="password"
            value={pendingNewPassword}
            onChange={(e) => setPendingNewPassword(e.target.value)}
            placeholder="Enter new password"
            autoComplete="new-password"
            disabled={isSubmitting}
            required
            data-testid="new-password-input"
          />
          <PasswordRequirements requirements={requirements} />
        </div>
        <div className="form-group">
          <label htmlFor="confirm-password">Confirm Password</label>
          <input
            id="confirm-password"
            type="password"
            value={pendingConfirmPassword}
            onChange={(e) => setPendingConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            autoComplete="new-password"
            disabled={isSubmitting}
            required
            data-testid="confirm-password-input"
          />
          {pendingConfirmPassword.length > 0 && !passwordsMatch && (
            <p className="rule-unmet" data-testid="password-mismatch">
              Passwords do not match
            </p>
          )}
        </div>
        <button
          type="submit"
          className="login-btn"
          disabled={!canSubmit}
          data-testid="set-password-btn"
        >
          {isSubmitting ? "Saving..." : "Save Password"}
        </button>
      </form>
      <button
        type="button"
        className="forgot-password-btn"
        onClick={onBack}
        disabled={isSubmitting}
      >
        Back to Login
      </button>
    </>
  );
};

export default NewPasswordForm;
